import React, { useState, useEffect } from "react";
import { getShopifyPrice } from "./shopify-prices";
import { formatCurrency } from "../../../shared/formatters/currency";
import { getCurrencyCookie } from "../../../shared/utils/cookies";
import { applyVat } from "../../../shared/utils/apply-vat";

const ShopifyPrice = ({ shopifyVariantId, fallbackPrice, loadingText = "Loading..." }) => {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!shopifyVariantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    getShopifyPrice(shopifyVariantId)
      .then((data) => {
        setPrice(data);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [shopifyVariantId]);

  if (loading) return <span>{loadingText}</span>;

  if (error || price == null) {
    if (fallbackPrice != null) return <span>{formatCurrency(applyVat(fallbackPrice), getCurrencyCookie())}</span>;
    return null;
  }

  return <span>{formatCurrency(parseFloat(price.amount), price.currencyCode)}</span>;
};

export const ShopifyTotalPrice = ({ items, fallbackTotal, loadingText = "Loading..." }) => {
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const itemsKey = items?.map(i => `${i.shopifyVariantId}:${i.quantity}`).join(',');

  useEffect(() => {
    if (!items?.length) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    Promise.all(
      items
        .filter(item => item.shopifyVariantId)
        .map(item =>
          getShopifyPrice(item.shopifyVariantId).then(data => ({
            amount: parseFloat(data.amount) * item.quantity,
            currencyCode: data.currencyCode,
          }))
        )
    )
    .then(results => {
      const currencyCode = results[0]?.currencyCode;
      const amount = results.reduce((acc, val) => acc + val.amount, 0);
      setTotal({ amount, currencyCode });
    })
    .catch(() => {
      setError(true);
    })
    .finally(() => setLoading(false));
  }, [itemsKey]);

  if (loading) return <span>{loadingText}</span>;

  if (error || total == null) {
    if (fallbackTotal != null) return <span>{formatCurrency(applyVat(fallbackTotal), getCurrencyCookie())}</span>;
    return null;
  }

  return <span>{formatCurrency(total.amount, total.currencyCode)}</span>;
};

export default ShopifyPrice;
